import { getTruthyValue } from "@draftkings/sportsbook-common-utils";
import { isSettlementStatusVoid, safeNumber, safeString, Tracker } from "../../helpers";
import { parseSettlementStatus } from "../parseSettlementStatus";
import { getFunctionHandler, telemetry } from "../../constants";
import { makeOpenBetBoostDetails } from "./makeOpenBetBoostDetails";
import { parseProfitBoostBonusMessagesAndStatus } from "./parseProfitBoostBonusMessagesAndStatus";
import { parseEarlyWinBonusMessagesAndState } from "./parseEarlyWinBonusMessagesAndState";
import { parseOddsBoostMessagesAndStatus } from "./parseOddsBoostBonusMessagesAndStatus";
import { parseQualifyingBetBonusMessagesAndState } from "./parseQualifyingBetBonusMessagesAndState";
import { parseGuaranteedPayoutBonusMessagesAndStatus } from "./parseGuaranteedPayoutBonusMessagesAndStatus";
import { getSelectionsLength } from "../parseBet/utils";
const getMaxWinningAmount = (boostMaxReturns) => boostMaxReturns;
const getWinningAmount = (boostReturns) => boostReturns;
const getBoostDisplayOdds = (boostDisplayOdds, boostOriginalOdds) => boostDisplayOdds || boostOriginalOdds;
const getOriginalBoostPercentage = (boostOriginalValue) => boostOriginalValue * 100;
export const getBoostPercentage = (boostValue, boostOriginalValue) => (boostValue !== null && boostValue !== void 0 ? boostValue : boostOriginalValue) * 100;
export function parsePlayerBonus(options) {
    if (!options.bet.bonus) {
        return undefined;
    }
    const parser = parlayBoostMap[options.bet.bonus.bonusType];
    return parser(Object.assign(Object.assign({}, options), { bonus: options.bet.bonus }));
}
const boostMap = {
    FixedExtension: (options) => parseOddsBoostFixed(options, "ODDS_BOOST_EXTENSION"),
    Fixed: (options) => parseOddsBoostFixed(options, "ODDS_BOOST_FIXED"),
    PercentNetProfit: (options) => parseProfitBoost(options, "PROFIT_BOOST"),
    PercentOdds: (options) => parseProfitBoost(options, "ODDS_BOOST_PERCENTAGE"),
    GuaranteedPayout: (options) => parseGuaranteedPayout(options),
};
const parlayBoostMap = new Proxy({
    FreeBet: parseFreeBet,
    BetBoost: (options) => boostMap[safeString(options.bonus.boostType)](options),
    EarlyWin: parseEarlyWin,
    RiskFreeBet: (options) => parseFreeBet(Object.assign(Object.assign({}, options), { isRiskFreeBet: true })),
    QualifyingBet: parseQualifyingBet,
}, getFunctionHandler);
function parseFreeBet({ bonus, dictionary, formatAmount, isRiskFreeBet = false }) {
    const amount = safeNumber(bonus.freeBetAmount);
    const formattedAmount = formatAmount(amount, "");
    return {
        type: "FREE_BET",
        amount,
        isRiskFree: isRiskFreeBet,
        label: isRiskFreeBet
            ? `${formattedAmount} ${dictionary.myBetsDictionary.RiskFreeBet}`
            : `${formattedAmount} ${dictionary.myBetsDictionary.FreeBet}`,
    };
}
function parseOddsBoostFixed({ bonus, bet, dictionary }, type) {
    const [status, messages] = parseOddsBoostMessagesAndStatus(bet.settlementStatus, dictionary);
    return {
        type,
        playerBonusId: safeString(bonus.bonusId),
        boostDisplayOdds: getBoostDisplayOdds(safeString(bonus.boostDisplayOdds), safeString(bonus.boostOriginalOdds)),
        originalBoostedOdds: safeString(bonus.boostOriginalOdds),
        maxWinningAmount: getMaxWinningAmount(safeNumber(bonus.boostMaxReturns)),
        winningsAmount: getWinningAmount(safeNumber(bonus.boostReturns)),
        label: dictionary.myBetsDictionary.OddsBoost,
        status,
        messages,
    };
}
function getProfitBoostLabel(betType, boostPercentage, dictionary) {
    return betType !== "Single"
        ? `+${boostPercentage}% ${dictionary.myBetsDictionary.ParlayBoost}`
        : `+${boostPercentage}% ${dictionary.myBetsDictionary.Boost}`;
}
function parseProfitBoost({ bonus, bet, dictionary, retriever, openModal, openPromosPage, }, type) {
    var _a;
    const [status, messages] = parseProfitBoostBonusMessagesAndStatus(bonus, bet, dictionary);
    const currentTab = safeString((_a = retriever.lastRequest().filter) === null || _a === void 0 ? void 0 : _a.status);
    const bonusId = safeString(bonus.bonusId);
    const boostSelections = bet.selections.filter(selection => selection.isInBetBoost && !isSettlementStatusVoid(parseSettlementStatus(selection.settlementStatus)));
    const originalBoostSelections = bet.selections.filter(selection => selection.isInBetBoost);
    const originalBoostPercentage = getOriginalBoostPercentage(safeNumber(bonus.boostOriginalValue));
    const boostPercentage = getBoostPercentage(bonus.boostValue, bonus.boostOriginalValue);
    return {
        type,
        playerBonusId: bonusId,
        boostDisplayOdds: getBoostDisplayOdds(safeString(bonus.boostDisplayOdds), safeString(bonus.boostOriginalOdds)),
        originalBoostedOdds: safeString(bonus.boostOriginalOdds),
        maxWinningAmount: getMaxWinningAmount(safeNumber(bonus.boostMaxReturns)),
        winningsAmount: getWinningAmount(safeNumber(bonus.boostReturns)),
        boostPercentage,
        originalBoostPercentage,
        messages,
        status,
        openPromosPage: () => {
            Tracker.getInstance().trackEvent(telemetry.events.myBets, {
                ClientType: telemetry.clientType,
                Action: telemetry.actions.myBets.openBoostMoreInfo,
                CurrentTab: currentTab,
                MyBetsVersion: telemetry.version,
            });
            openPromosPage();
        },
        openBoostDetails: makeOpenBetBoostDetails({
            retriever,
            bonusId,
            dictionary,
            boostSelections: getSelectionsLength(boostSelections),
            originalBoostSelections: getSelectionsLength(originalBoostSelections),
            openModal,
        }),
        label: getProfitBoostLabel(bet.type, boostPercentage, dictionary)
    };
}
function parseEarlyWin({ bet, bonus, dictionary, }) {
    const [status, messages] = parseEarlyWinBonusMessagesAndState(bet, dictionary);
    return {
        type: "EARLY_WIN",
        playerBonusId: safeString(bonus.bonusId),
        label: `${dictionary.myBetsDictionary.UP} ${bonus.earlyWinToken} ${dictionary.myBetsDictionary.EarlyWin}`,
        messages,
        status,
    };
}
function parseQualifyingBet({ bet, bonus, dictionary, }) {
    const [status, messages] = parseQualifyingBetBonusMessagesAndState(bet, dictionary);
    const settlementStatus = parseSettlementStatus(bet.settlementStatus);
    const qualifyingBetType = getTruthyValue(bonus.qualifyingBetType, "");
    const qualifyingBetTypeString = getTruthyValue(dictionary[qualifyingBetType], qualifyingBetType);
    const qualifyingBetTypeKey = qualifyingBetType === "Second Chance" ? "QualifyingBetNotAppliedSecondChance" : "QualifyingBetNotApplied";
    return {
        type: "QUALIFYING_BET",
        playerBonusId: safeString(bonus.bonusId),
        label: dictionary.myBetsDictionary.QualifyingBet.replace("{0}", safeString(qualifyingBetTypeString.toLocaleUpperCase())),
        messages,
        status,
        helperMessage: settlementStatus === "Lost" && (qualifyingBetType === "No Sweat" || qualifyingBetType === "Second Chance") ? dictionary.myBetsDictionary[qualifyingBetTypeKey].replace("{0}", qualifyingBetTypeString) : ""
    };
}
function parseGuaranteedPayout({ bonus, bet, dictionary, }) {
    const [status, messages] = parseGuaranteedPayoutBonusMessagesAndStatus(bonus, bet, dictionary);
    return {
        type: "GUARANTEED_PAYOUT",
        playerBonusId: safeString(bonus.bonusId),
        label: dictionary.myBetsDictionary.GuaranteedPayout,
        status,
        messages,
        boostStatus: getTruthyValue(bonus.boostStatus, ""),
        boostDisplayOdds: getBoostDisplayOdds(safeString(bonus.boostDisplayOdds), safeString(bonus.boostOriginalOdds)),
        maxWinningAmount: getMaxWinningAmount(safeNumber(bonus.boostMaxReturns)),
        winningsAmount: getWinningAmount(safeNumber(bonus.boostReturns)),
    };
}
//# sourceMappingURL=parsePlayerBonus.js.map